import { env } from "@/lib/env";
import { logger } from "@/lib/logger";

/**
 * Central error reporting over a pluggable sink (ENGINEERING.md §6, DECISIONS
 * ADR-0006). Call sites use `reportError()` and never import a vendor SDK.
 *
 *  - The default sink logs through the central `logger` (which redacts
 *    tokens/PII), so reporting works in dev with zero config.
 *  - Additional sinks register via `registerErrorSink()`. The Sentry sink is
 *    DSN-gated and registered lazily once `@sentry/nextjs` is wired (the dep is
 *    not installed yet — see the hook below). A backend error-log sink can be
 *    added the same way (BACKEND_ASKS §7).
 */
export interface ErrorContext {
  /** Where it happened, e.g. "boundary:root", "query", "window.onerror". */
  source?: string;
  /** Any extra, already-safe-to-log breadcrumbs. */
  [key: string]: unknown;
}

export interface ErrorSink {
  readonly name: string;
  report(error: unknown, context?: ErrorContext): void;
}

const sinks: ErrorSink[] = [];

export function registerErrorSink(sink: ErrorSink) {
  if (sinks.some((s) => s.name === sink.name)) return;
  sinks.push(sink);
}

/** Normalize anything thrown into a real Error for consistent reporting. */
function toError(value: unknown): Error {
  if (value instanceof Error) return value;
  if (typeof value === "string") return new Error(value);
  try {
    return new Error(JSON.stringify(value));
  } catch {
    return new Error(String(value));
  }
}

/** Report an error to every registered sink. Never throws. */
export function reportError(error: unknown, context?: ErrorContext) {
  const err = toError(error);
  for (const sink of sinks) {
    try {
      sink.report(err, context);
    } catch {
      // A sink must never break the app or other sinks.
    }
  }
}

/** Default sink: structured log line. Always present. */
registerErrorSink({
  name: "logger",
  report(error, context) {
    const err = toError(error);
    logger.error(err.message, {
      ...context,
      name: err.name,
      stack: err.stack,
    });
  },
});

/**
 * DSN-gated Sentry registration. With no `NEXT_PUBLIC_SENTRY_DSN` this is a
 * no-op and `@sentry/nextjs` is never imported on the client, so the build
 * carries no vendor weight. With a DSN, the SDK is initialized by
 * `sentry.client.config.ts` (which drops Sentry's own global handlers — see
 * ADR-0006); here we add the sink so every `reportError()` — query failures,
 * boundary catches, window errors — reaches Sentry through the single funnel.
 *
 * Idempotent: `registerErrorSink` dedupes by name, so repeated calls (e.g. from
 * the providers effect across renders) register the sink once.
 */
export function initErrorReporting() {
  if (!env.sentryDsn) return;

  void import("@sentry/nextjs").then((Sentry) => {
    registerErrorSink({
      name: "sentry",
      report(error, context) {
        Sentry.captureException(
          error,
          context ? { extra: context } : undefined,
        );
      },
    });
  });
}
